import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { MDXProvider } from "@mdx-js/react";

/**
 * Maps MDX-emitted HTML elements to brand-styled React nodes. We don't pull
 * `@tailwindcss/typography` because our typography is already brand-tuned
 * (Inter Display + JetBrains Mono + ink/paper palette) and `prose` would
 * fight that. Keeps the dep surface small and the visual identity coherent
 * with the rest of the site.
 */
export const proseClassNames = {
  h1: "font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink dark:text-paper mt-12 mb-4",
  h2: "font-display text-2xl sm:text-3xl font-semibold tracking-tight text-ink dark:text-paper mt-12 mb-4",
  h3: "font-display text-xl sm:text-2xl font-semibold tracking-tight text-ink dark:text-paper mt-10 mb-3",
  p: "text-[15px] leading-relaxed text-ink/80 dark:text-paper/80 mb-5",
  a: "underline decoration-ink/30 dark:decoration-paper/30 hover:decoration-ink dark:hover:decoration-paper transition-colors",
  ul: "list-disc ml-5 mb-5 space-y-1.5 text-ink/80 dark:text-paper/80",
  ol: "list-decimal ml-5 mb-5 space-y-1.5 text-ink/80 dark:text-paper/80",
  li: "leading-relaxed",
  blockquote: "border-l-2 border-ink/20 dark:border-paper/20 pl-4 my-6 italic text-ink/70 dark:text-paper/70",
  code: "font-mono text-[0.85em] bg-ink/[0.06] dark:bg-paper/[0.08] rounded px-1.5 py-0.5",
  pre: "font-mono text-[13px] leading-relaxed bg-ink/[0.04] dark:bg-paper/[0.04] border border-ink/10 dark:border-paper/12 rounded-2xl p-5 my-6 overflow-x-auto",
  hr: "my-10 border-ink/10 dark:border-paper/10",
};

export const mdxComponents = {
  h1: (props: ComponentPropsWithoutRef<"h1">) => (
    <h1
      {...props}
      className={proseClassNames.h1}
    />
  ),
  h2: (props: ComponentPropsWithoutRef<"h2">) => (
    <h2
      {...props}
      className={proseClassNames.h2}
    />
  ),
  h3: (props: ComponentPropsWithoutRef<"h3">) => (
    <h3
      {...props}
      className={proseClassNames.h3}
    />
  ),
  p: (props: ComponentPropsWithoutRef<"p">) => (
    <p
      {...props}
      className={proseClassNames.p}
    />
  ),
  a: (props: ComponentPropsWithoutRef<"a">) => (
    <a
      {...props}
      className={proseClassNames.a}
    />
  ),
  ul: (props: ComponentPropsWithoutRef<"ul">) => (
    <ul {...props} className={proseClassNames.ul} />
  ),
  ol: (props: ComponentPropsWithoutRef<"ol">) => (
    <ol {...props} className={proseClassNames.ol} />
  ),
  li: (props: ComponentPropsWithoutRef<"li">) => (
    <li {...props} className={proseClassNames.li} />
  ),
  blockquote: (props: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote
      {...props}
      className={proseClassNames.blockquote}
    />
  ),
  code: (props: ComponentPropsWithoutRef<"code">) => (
    <code
      {...props}
      className={proseClassNames.code}
    />
  ),
  pre: (props: ComponentPropsWithoutRef<"pre">) => (
    <pre
      {...props}
      className={proseClassNames.pre}
    />
  ),
  hr: (props: ComponentPropsWithoutRef<"hr">) => (
    <hr {...props} className={proseClassNames.hr} />
  ),
};

export function MdxProse({ children }: { children: ReactNode }) {
  return <MDXProvider components={mdxComponents}>{children}</MDXProvider>;
}
